import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { Roles } from '../../../../../../constants';
import { providerActions } from '../../../../../../store';
import { fetchProviderById } from '../../../services';
import { buildRelationshipManagerPatch } from '../../../utils';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { SelectValueType } from '../../../../../../utils/types';

export function useProviderRelationshipManager() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { providerId } = useParams();
  const {
    lastFetchedProvider: provider,
    relationshipManagerCandidates,
    relationshipManagerAssignments,
  } = useSelector((state: RootState) => state.providers);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRelationshipManagerId, setSelectedRelationshipManagerId] =
    useState<SelectValueType>('');
  const confirmedRelationshipManagerIdRef = useRef<SelectValueType>('');
  const updateRequestRef = useRef<{ abort: () => void } | null>(null);
  const updateGenerationRef = useRef(0);

  const isGuardianProvider = Boolean(provider?.roles?.includes(Roles.Guardian));

  useEffect(() => {
    if (!providerId || !isGuardianProvider) {
      setLoading(false);
      return;
    }

    let isActive = true;
    const activeRequests: Array<{ abort: () => void }> = [];

    const loadRelationshipManagerData = async () => {
      setLoading(true);
      dispatch(providerActions.clearRelationshipManagerData());

      const candidatesRequest = dispatch(
        providerActions.getRelationshipManagerCandidates({
          getAccessTokenSilently,
          role: Roles.ClientSuccessManager,
        }),
      );
      const assignmentsRequest = dispatch(
        providerActions.getRelationshipManagerAssignments({
          getAccessTokenSilently,
          providerId,
        }),
      );
      activeRequests.push(candidatesRequest, assignmentsRequest);
      await Promise.all([candidatesRequest, assignmentsRequest]);

      if (isActive) {
        setLoading(false);
      }
    };

    loadRelationshipManagerData();

    return () => {
      isActive = false;
      activeRequests.forEach((request) => request.abort());
      dispatch(providerActions.clearRelationshipManagerData());
    };
  }, [dispatch, getAccessTokenSilently, isGuardianProvider, providerId]);

  useEffect(() => {
    if (saving) return;
    const confirmedId =
      relationshipManagerAssignments[0]?.relationship_manager_provider?.id || '';
    confirmedRelationshipManagerIdRef.current = confirmedId;
    setSelectedRelationshipManagerId(confirmedId);
  }, [relationshipManagerAssignments, saving]);

  useEffect(() => {
    return () => {
      updateGenerationRef.current += 1;
      updateRequestRef.current?.abort();
      updateRequestRef.current = null;
    };
  }, []);

  const handleUpdateRelationshipManager = async (value: SelectValueType) => {
    if (!providerId || !isGuardianProvider || !provider) return;

    const generation = updateGenerationRef.current + 1;
    updateGenerationRef.current = generation;

    updateRequestRef.current?.abort();
    const abortController = new AbortController();
    updateRequestRef.current = { abort: () => abortController.abort() };
    setSelectedRelationshipManagerId(value);
    setSaving(true);

    try {
      const latestProvider = await fetchProviderById(
        providerId,
        getAccessTokenSilently,
        abortController.signal,
      );
      if (generation !== updateGenerationRef.current) return;

      dispatch(providerActions.updateProviderLists(latestProvider));
      const data = buildRelationshipManagerPatch(
        Boolean(latestProvider.roles?.includes(Roles.Guardian)),
        value,
        latestProvider,
      );
      if (data == null || !('relationship_manager_provider_id' in data)) {
        setSelectedRelationshipManagerId(confirmedRelationshipManagerIdRef.current);
        if (data == null) {
          toast.error('Select a valid relationship manager');
        }
        return;
      }

      const updateRequest = dispatch(
        providerActions.updateProviderById({
          getAccessTokenSilently,
          providerId,
          data,
        }),
      );
      updateRequestRef.current = updateRequest;
      const updateResult = await updateRequest;

      if (generation !== updateGenerationRef.current) return;

      updateRequestRef.current = null;

      if (updateResult.meta.aborted) {
        setSelectedRelationshipManagerId(confirmedRelationshipManagerIdRef.current);
        return;
      }

      if (providerActions.updateProviderById.fulfilled.match(updateResult)) {
        const assignmentsRequest = dispatch(
          providerActions.getRelationshipManagerAssignments({
            getAccessTokenSilently,
            providerId,
          }),
        );
        updateRequestRef.current = assignmentsRequest;
        const assignmentsResult = await assignmentsRequest;

        if (generation !== updateGenerationRef.current) return;

        updateRequestRef.current = null;

        if (assignmentsResult.meta.aborted) {
          setSelectedRelationshipManagerId(confirmedRelationshipManagerIdRef.current);
          return;
        }

        if (
          providerActions.getRelationshipManagerAssignments.fulfilled.match(assignmentsResult)
        ) {
          toast.success('Relationship manager updated successfully', {
            toastId: 'relationship-manager-updated',
          });
        } else {
          setSelectedRelationshipManagerId(confirmedRelationshipManagerIdRef.current);
        }
      } else {
        setSelectedRelationshipManagerId(confirmedRelationshipManagerIdRef.current);
        toast.error(updateResult.error.message);
      }
    } catch (error) {
      if (generation !== updateGenerationRef.current) return;
      setSelectedRelationshipManagerId(confirmedRelationshipManagerIdRef.current);
      if (error instanceof Error && error.name === 'AbortError') return;
      toast.error(
        error instanceof Error ? error.message : 'Unable to update relationship manager',
      );
    } finally {
      if (generation === updateGenerationRef.current) {
        setSaving(false);
      }
    }
  };

  const handleClearRelationshipManager = () => {
    handleUpdateRelationshipManager('');
  };

  return {
    isGuardianProvider,
    loading,
    saving,
    relationshipManagerCandidates,
    selectedRelationshipManagerId,
    handleUpdateRelationshipManager,
    handleClearRelationshipManager,
  };
}
